import { useState } from "react";
import { FaUserClock, FaCalendarAlt, FaStickyNote } from "react-icons/fa";

import Modal from "../modal/modal";
import Dropdown from "../modal/dropdown";
import DatePicker from "../modal/datepicker";

import { toast } from "react-toastify";
import { parseFieldErrors } from "../../utils/errors";

import { createLeaveRequest } from "../services/attendanceservice";
import { LeaveTypeOptions, LeaveDayPart, LeaveDayPartOptions } from "../../constants/constants";

/**
 * Apply-leave form, rendered as a modal.
 *
 * Props:
 *  - employees:  flat employee list, only needed (and only rendered) when canApprove is true
 *  - canApprove: whether the current user can raise a leave request on someone else's behalf
 *  - onClose:    close the modal without saving
 *  - onSaved:    called after a successful submit — parent decides what to do (close + refetch)
 */
const ApplyLeave = ({ employees, canApprove, onClose, onSaved }) => {
  const [form, setForm] = useState({
    employee: "",
    leave_type: "",
    start_date: "",
    end_date: "",
    day_part: LeaveDayPart.FULL_DAY,
    reason: "",
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [fieldErrors, setFieldErrors] = useState({});

  const clearFieldError = (field) => {
    if (fieldErrors[field]) {
      setFieldErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const handleSave = async () => {
    const errs = {};

    if (!form.leave_type) errs.leave_type = "Leave type is required";
    if (!form.start_date) errs.start_date = "Start date is required";
    if (!form.end_date) errs.end_date = "End date is required";
    if (!form.reason) errs.reason = "Reason is required";

    if (Object.keys(errs).length > 0) {
      setFieldErrors(errs);
      setError("Please check the field errors below.");
      return;
    }

    setSaving(true);
    setError("");
    setFieldErrors({});

    try {
      const payload = { ...form };
      if (!canApprove) delete payload.employee;

      await createLeaveRequest(payload);
      toast.success("Leave request submitted");
      onSaved();
    } catch (err) {
      setFieldErrors(parseFieldErrors(err));
      setError(err.response?.data?.message || "Failed to submit leave");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal title="Apply for Leave" onClose={onClose} size="lg">
      {error && <div className="form-error">{error}</div>}

      <div className="attendance-form-body">
        {/* EMPLOYEE — only shown to people applying on someone else's behalf */}
        {canApprove && (
          <div className="form-section-card">
            <div className="section-title-premium">
              <FaUserClock />
              Employee Details
            </div>

            <div className="full-row">
              <Dropdown
                options={employees.map((employee) => ({
                  label: `${employee.full_name || employee.name} (${employee.emp_id})`,
                  value: employee.id,
                }))}
                value={form.employee}
                onChange={(value) => {
                  setForm({ ...form, employee: value });
                  clearFieldError("employee");
                }}
                placeholder="Select Employee"
              />
              {fieldErrors.employee && (
                <div className="field-error">{fieldErrors.employee}</div>
              )}
            </div>
          </div>
        )}

        {/* LEAVE DETAILS */}
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaCalendarAlt />
            Leave Details
          </div>

          <div className="grid-3">
            {/* LEAVE TYPE — sourced directly from LeaveTypeOptions in constants.js */}
            <div>
              <label>Leave Type</label>
              <Dropdown
                options={LeaveTypeOptions}
                value={form.leave_type}
                onChange={(value) => {
                  setForm({ ...form, leave_type: value });
                  clearFieldError("leave_type");
                }}
                placeholder="Leave Type *"
              />
              {fieldErrors.leave_type && (
                <div className="field-error">{fieldErrors.leave_type}</div>
              )}
            </div>

            {/* START DATE */}
            <div>
              <label>Start Date</label>
              <DatePicker
                className={fieldErrors.start_date ? "input-error" : ""}
                value={form.start_date}
                onChange={(value) => {
                  setForm({ ...form, start_date: value });
                  clearFieldError("start_date");
                }}
                placeholder="Start Date *"
              />
              {fieldErrors.start_date && (
                <div className="field-error">{fieldErrors.start_date}</div>
              )}
            </div>

            {/* END DATE */}
            <div>
              <label>End Date</label>
              <DatePicker
                className={fieldErrors.end_date ? "input-error" : ""}
                value={form.end_date}
                onChange={(value) => {
                  setForm({ ...form, end_date: value });
                  clearFieldError("end_date");
                }}
                minDate={form.start_date}
                placeholder="End Date *"
              />
              {fieldErrors.end_date && (
                <div className="field-error">{fieldErrors.end_date}</div>
              )}
            </div>

            {/* DAY PART — sourced from LeaveDayPartOptions in constants.js (numeric
                ids matching LeaveDayPart, same pattern as LeaveType/LeaveStatus)
                so it can't drift out of sync with what the backend accepts */}
            <div>
              <label>Day Part</label>
              <Dropdown
                options={LeaveDayPartOptions}
                value={form.day_part}
                onChange={(value) => setForm({ ...form, day_part: value })}
                placeholder="Day Part"
              />
              {fieldErrors.day_part && (
                <span className="field-error">{fieldErrors.day_part}</span>
              )}
            </div>
          </div>
        </div>

        {/* REASON */}
        <div className="form-section-card">
          <div className="section-title-premium">
            <FaStickyNote />
            Reason
          </div>

          <div>
            <textarea
              className={fieldErrors.reason ? "input-error" : ""}
              placeholder="Reason for leave *"
              value={form.reason}
              onChange={(e) => {
                setForm({ ...form, reason: e.target.value });
                clearFieldError("reason");
              }}
            />
            {fieldErrors.reason && (
              <div className="field-error">{fieldErrors.reason}</div>
            )}
          </div>
        </div>
      </div>

      <div
        className="modal-footer"
        style={{ marginLeft: -26, marginRight: -26, marginBottom: -22, marginTop: 20 }}
      >
        <div className="modal-actions">
          <button className="primary" onClick={handleSave} disabled={saving}>
            {saving ? "Submitting…" : "Submit"}
          </button>
          <button className="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default ApplyLeave;